import { ReactNode } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { BodilColor } from '~src/branding';
import { DeepPartial, EnergyLabel as EnergyLabelType, Residence, ResidenceHeating } from '~src/model';
import { calculateResidenceEnergyLabel } from '~src/util';

import { Icon } from './Icon';
import { Heading } from './Typography';

const FONT_SIZE = 12;
const ASPECT_WIDTH = 22;
const ASPECT_HEIGHT = 28;

type BoxProps = {
  children?: ReactNode;
  style?: CSSProperties;
} & Pick<EnergyLabelProps, 'width' | 'mark'>;

const Box = ({ width: inputWidth, mark, children, style }: BoxProps) => {
  const color = backgroundColorByMark(mark);
  const width = inputWidth ?? 30;
  const height = (width / ASPECT_WIDTH) * ASPECT_HEIGHT;

  return (
    <div style={{ width, height, position: 'relative', ...style }}>
      <Icon.EnergyLabel
        width={width}
        height={height}
        color={color}
        style={{
          fontSize: FONT_SIZE,
          lineHeight: `${FONT_SIZE}px`,
          aspectRatio: `${ASPECT_WIDTH} / ${ASPECT_HEIGHT}`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          fontWeight: 600,
        }}
      />
      {children}
    </div>
  );
};

const Label = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: BodilColor.base.white,
});

type EnergyLabelProps = {
  width?: number;
  mark: EnergyLabelType | '?';
  style?: CSSProperties;
};

const EnergyLabel = ({ width, mark, style }: EnergyLabelProps) => {
  const subtitle = mark.slice(1);

  return (
    <Box style={style} {...{ width, mark }}>
      <Label
        style={{
          top: subtitle ? 5 : 10,
          position: 'absolute',
          left: '50%',
          transform: 'translate(-50%, 0)',
        }}
      >
        <Heading level={2} style={{ color: BodilColor.base.white }}>
          {mark[0]}
        </Heading>

        <Heading
          level={4}
          style={{
            color: BodilColor.base.white,
            lineHeight: '8px',
            fontSize: '10px',
          }}
        >
          {subtitle}
        </Heading>
      </Label>
    </Box>
  );
};

type ResidenceEnergyLabelProps = Pick<EnergyLabelProps, 'width'> & {
  residence?: DeepPartial<Residence>;
  style?: CSSProperties;
};

export const ResidenceEnergyLabel = ({ residence, style, width }: ResidenceEnergyLabelProps) => {
  const { area, energyLabel, primaryHeating, secondaryHeating } = residence ?? {};

  if (energyLabel) {
    return <EnergyLabel mark={energyLabel} style={style} width={width} />;
  }

  if (!primaryHeating || !area) {
    return <EnergyLabel mark='?' style={style} width={width} />;
  }

  const { label } = calculateResidenceEnergyLabel({
    primaryHeating: primaryHeating as ResidenceHeating,
    secondaryHeating: secondaryHeating as ResidenceHeating,
    area,
  });

  return <EnergyLabel mark={label as EnergyLabelType} style={style} width={width} />;
};

const backgroundColorByMark = (mark: EnergyLabelType | '?'): string => {
  switch (mark) {
    case 'A2020': {
      return '#037D39';
    }
    case 'A2015': {
      return '#009A32';
    }
    case 'A2010': {
      return '#6CC534';
    }
    case 'B': {
      return '#58A81C';
    }
    case 'C': {
      return '#DE9C06';
    }
    case 'D': {
      return '#E47D01';
    }
    case 'E': {
      return '#E84713';
    }
    case 'F': {
      return '#900006';
    }
    case 'G': {
      return '#632757';
    }
  }

  return BodilColor.greyscale[30];
};
